// Сплэш из index.html (public/splash.js) — прогресс и уход. Ступени: бандл → /meta → вход → bootstrap →
// предзагрузка ступени first из манифеста и первый кадр сцены. Убирается плавно, когда главный готов;
// системный экран (ошибка, техработы, нет сети) или «Не грузится» показываются сразу, без сплэша поверх.
import { effect } from '@preact/signals';
import type { App } from '../app/app.js';
import { stageReady } from '../ui/App.js';
import { t as tr } from '../i18n/t.js';
import { bootUi } from './boot.js';
import { animate, DUR, EASE, stagger } from '../ui/motion.js';

interface SplashApi { fp: number; minAt?: number; set(p: number, text?: string): void; done(): void }

/** Сколько ждать картинки ступени first и сцену после входа, прежде чем всё равно открыть главный. */
export const SPLASH_ASSETS_CAP_MS = 3000;

const api = (): SplashApi | undefined => (globalThis as { __splash?: SplashApi }).__splash;

export function splashSet(p: number, text?: string): void { api()?.set(p, text); }

let finished = false;
let bootDoneMs = 0;
let packMs = 0;
let stageMs = 0;
/** immediate — системный экран или «Не грузится»: без выдержки, сразу. */
export function splashDone(immediate = false): void {
  if (finished) return;
  finished = true;
  // Быстрая загрузка: сплэш держится до minAt (подсказка сменилась хотя бы раз), потом уходит вместе с «оседанием» главного.
  const wait = immediate ? 0 : (api()?.minAt ?? 0) - performance.now();
  if (wait > 0) { setTimeout(finishNow, wait); return; }
  finishNow();
}

function finishNow(): void {
  api()?.done();
  revealMain();
  const fp = api()?.fp ?? 0;
  (globalThis as { __splashTimes?: unknown }).__splashTimes = { firstFrameMs: Math.round(fp), bootMs: Math.round(bootDoneMs), packMs: Math.round(packMs), stageMs: Math.round(stageMs), readyMs: Math.round(performance.now()) };
}

/**
 * Уход сплэша → главный: пока сплэш тает, сцена и DOM-слой «оседают» из лёгкого приближения (одинаковое
 * преобразование у холста и у слоя — метки зон не разъезжаются с картинкой), затем шапка, полоса действия и
 * навигация поднимаются ступеньками. Прозрачность главного не трогаем — под тающим сплэшем не бывает тёмной вспышки.
 */
function revealMain(): void {
  const doc = globalThis.document;
  if (!doc || doc.querySelector('#ui > .system')) return;
  const settle = [{ transform: 'scale(1.06)' }, { transform: 'none' }];
  for (const el of [doc.querySelector('#stage canvas'), doc.querySelector('#ui > .layout')]) animate(el, settle, { duration: DUR.scene + 200, easing: EASE.out, fill: 'none' });
  stagger(doc.querySelectorAll('#ui > .layout .res-header, #ui > .layout .action-bar, #ui > .layout .nav-bar, #ui > .layout .side-nav, #ui > .layout .home-fab'), 'rise', { delay: 220, step: 60 });
}

/** Картинки ступени: промис на загрузку всех (ошибка картинки не держит сплэш). */
export function loadImages(urls: readonly string[]): Promise<void> {
  return Promise.all(urls.map((u) => new Promise<void>((res) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = img.onerror = () => res();
    img.src = u;
  }))).then(() => {});
}

/**
 * Связать сплэш с запуском: прогресс по bootUi, «Медленная сеть» текстом, уход по готовности главного.
 * firstPack — промис предзагрузки ступени first (после манифеста).
 */
export function bindSplash(app: App, firstPack: Promise<void>): void {
  splashSet(0.2);
  let packDone = false;
  void firstPack.then(() => { packDone = true; packMs = performance.now(); splashSet(0.95); tryFinish(); });
  let bootDoneAt = 0;
  const tryFinish = () => {
    if (finished || !bootDoneAt) return;
    if ((packDone && stageReady.value) || performance.now() - bootDoneAt > SPLASH_ASSETS_CAP_MS) splashDone();
  };
  const stopBoot = effect(() => {
    const ui = bootUi.value;
    splashSet(0.2 + ui.progress * 0.55, ui.slow ? tr('CL-SCREENS-SYSTEM-12') : undefined);
    if (ui.stuck) splashDone(true); // SCR-BOOT с «Повторить» должен быть виден
  });
  const stopStage = effect(() => { if (stageReady.value) { stageMs ||= performance.now(); tryFinish(); } });
  const onNav = () => {
    const sys = app.nav.state.system;
    if (sys && !(sys.id === 'SCR-BOOT' && !sys.params['code'])) { splashDone(true); return; }
    if (!sys && !bootDoneAt) {
      bootDoneAt = performance.now();
      bootDoneMs = bootDoneAt;
      splashSet(0.8);
      tryFinish();
      setTimeout(tryFinish, SPLASH_ASSETS_CAP_MS + 50);
    }
  };
  const unsub = app.nav.subscribe(onNav);
  onNav();
  const check = setInterval(() => { if (finished) { clearInterval(check); unsub(); stopBoot(); stopStage(); } }, 500);
}
